import { useCallback, useEffect, useRef, useState } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useApi } from "@/lib/api";
import { fmtDT } from "@/lib/brand";
import { Radio, CircleParking, RefreshCw, Layers } from "lucide-react";
import { GoogleFleetMap, MAPS_KEY } from "@/components/site/GoogleMaps";

const LAYERS = {
  google: { label: "Google", url: "https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}", sub: [], attr: "© Google" },
  "google-sat": { label: "Google Satellite", url: "https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}", sub: [], attr: "© Google" },
  osm: { label: "OpenStreetMap", url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", sub: ["a", "b", "c"], attr: "© OpenStreetMap" },
};

const pin = (status, source) => {
  const color = status === "on_trip" ? "#0ea5e9" : "#16a34a";
  const ring = source === "live" ? `<circle cx="18" cy="18" r="16" fill="${color}" opacity="0.25"><animate attributeName="r" values="10;17;10" dur="1.6s" repeatCount="indefinite"/></circle>` : "";
  return L.divIcon({
    className: "", iconSize: [36, 36], iconAnchor: [18, 18],
    html: `<svg width="36" height="36" viewBox="0 0 36 36">${ring}<circle cx="18" cy="18" r="9" fill="${color}" stroke="white" stroke-width="3"/></svg>`,
  });
};

export default function AdminFleet() {
  const api = useApi();
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const markersRef = useRef({});
  const [layer, setLayer] = useState("google");
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);

  const load = useCallback(() => api.get("/admin/fleet").then((r) => setData(r.data)).catch(() => setData((d) => d || { cars: [], counts: { on_trip: 0, live: 0, waiting: 0 }, hub: null })), [api]);
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (mapRef.current || !data) return;
    mapRef.current = L.map("fleet-map", { zoomControl: true, attributionControl: true }).setView([data.hub.lat, data.hub.lng], 12);
    L.marker([data.hub.lat, data.hub.lng], { icon: L.divIcon({ className: "", iconSize: [20, 20], iconAnchor: [10, 10], html: `<div style="width:16px;height:16px;border-radius:4px;background:#FF5A00;border:3px solid white;box-shadow:0 0 0 2px #FF5A00"></div>` }) })
      .addTo(mapRef.current).bindPopup(`<b>${data.hub.name}</b>`);
  }, [data]);

  useEffect(() => {
    if (!mapRef.current) return;
    if (layerRef.current) mapRef.current.removeLayer(layerRef.current);
    const l = LAYERS[layer];
    layerRef.current = L.tileLayer(l.url, { subdomains: l.sub, attribution: l.attr, maxZoom: 20 }).addTo(mapRef.current);
  }, [layer, data]);

  useEffect(() => {
    if (!mapRef.current || !data) return;
    data.cars.forEach((c) => {
      if (c.lat == null || c.lng == null) { if (markersRef.current[c.id]) { mapRef.current.removeLayer(markersRef.current[c.id]); delete markersRef.current[c.id]; } return; }
      let m = markersRef.current[c.id];
      if (!m) {
        m = L.marker([c.lat, c.lng], { icon: pin(c.status, c.source) }).addTo(mapRef.current).on("click", () => setSel(c));
        markersRef.current[c.id] = m;
      } else {
        m.setLatLng([c.lat, c.lng]);
        m.setIcon(pin(c.status, c.source));
      }
      m.bindTooltip(c.name, { direction: "top" });
    });
  }, [data]);

  return (
    <div className="space-y-6" data-testid="admin-fleet">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-semibold tracking-tight">Fleet tracking</h1>
        <div className="flex items-center gap-2">
          {!MAPS_KEY && <div className="flex items-center gap-1 rounded-md border border-white/10 bg-[#141414] p-1">
            <Layers className="ml-1 h-4 w-4 text-neutral-500" />
            {Object.entries(LAYERS).map(([k, v]) => (
              <button key={k} onClick={() => setLayer(k)} data-testid={`fleet-layer-${k}`}
                className={`rounded px-2.5 py-1 text-xs font-medium ${layer === k ? "bg-[#FF5A00] text-white" : "text-neutral-400 hover:text-white"}`}>{v.label}</button>
            ))}
          </div>}
          <button onClick={load} data-testid="fleet-refresh" className="rounded-md border border-white/10 p-2 hover:bg-white/5"><RefreshCw className="h-4 w-4" /></button>
        </div>
      </div>
      {data && (
        <div className="grid grid-cols-3 gap-4">
          {[["On trip", data.counts.on_trip, "text-sky-400"], ["Live GPS", data.counts.live, "text-emerald-400"], ["Waiting for signal", data.counts.waiting, "text-[#D4AF67]"]].map(([l, v, c]) => (
            <div key={l} className="rounded-lg border border-white/10 bg-[#141414] p-4">
              <p className="text-xs uppercase tracking-[0.15em] text-neutral-500">{l}</p>
              <p className={`mt-1 font-display text-2xl font-semibold ${c}`} data-testid={`fleet-count-${l.toLowerCase().replace(/\s/g, "-")}`}>{v}</p>
            </div>
          ))}
        </div>
      )}
      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        {MAPS_KEY && data && <GoogleFleetMap data={data} onSelect={setSel} />}
        <div id="fleet-map" data-testid="fleet-map" className={`h-[560px] w-full overflow-hidden rounded-lg border border-white/10 ${MAPS_KEY ? "hidden" : ""}`} style={{ background: "#1a1a1a" }} />
        <div className="space-y-2 lg:max-h-[560px] lg:overflow-y-auto">
          {(data?.cars || []).length === 0 && <p className="rounded-lg border border-white/10 bg-[#141414] p-6 text-center text-sm text-neutral-500" data-testid="fleet-empty">No active trips right now. Cars appear here only while a customer is on an ongoing rental.</p>}
          {(data?.cars || []).map((c) => (
            <button key={c.id} onClick={() => { setSel(c); if (c.lat != null) { mapRef.current?.setView([c.lat, c.lng], 14); markersRef.current[c.id]?.openTooltip(); } }}
              data-testid={`fleet-car-${c.id}`} className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors duration-150 ${sel?.id === c.id ? "border-[#FF5A00] bg-white/5" : "border-white/10 hover:bg-white/5"}`}>
              <img src={c.image} alt="" className="h-12 w-16 rounded object-cover" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{c.name}</p>
                <p className="flex items-center gap-1.5 text-xs">
                  <span className="flex items-center gap-1 text-sky-400"><Radio className="h-3 w-3" />On trip</span>
                  <span className="text-neutral-500">· {c.source === "live" ? "live GPS" : c.source === "stale" ? "signal lost" : "waiting for signal"}</span>
                </p>
                {c.trip?.customer_name && <p className="text-[11px] text-neutral-400">{c.trip.customer_name}{c.trip.customer_phone ? ` · ${c.trip.customer_phone}` : ""}</p>}
                {c.updated_at && <p className="text-[11px] text-neutral-500">Updated {fmtDT(c.updated_at)}</p>}
              </div>
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-neutral-500">
        Only cars on an active booking are shown. Their location comes live from the customer's app while the trip is ongoing, and disappears from the map the moment the trip is ended.
      </p>
    </div>
  );
}
